import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { LoginRequest, SessionResponse, SessionUser } from "@shared";
import { apiFetch, apiPost } from "../api/client";

interface SessionContextValue {
  /** null = deconnecte. undefined = on ne sait pas encore. */
  user: SessionUser | null | undefined;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * Etat de connexion du joueur. La session vit dans un cookie httpOnly : le
 * JavaScript ne peut pas la lire, il demande au serveur qui est connecte.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    apiFetch<SessionResponse>("/auth/me")
      .then((data) => {
        if (!cancelled) setUser(data.user);
      })
      .catch(() => {
        // Hors-ligne au demarrage : on considere le joueur deconnecte plutot
        // que de bloquer l'app sur un ecran de chargement infini.
        if (!cancelled) setUser(null);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (credentials: LoginRequest) => {
    const data = await apiPost<SessionResponse>("/auth/login", credentials);
    setUser(data.user);
  }, []);

  const register = useCallback(async (credentials: LoginRequest) => {
    const data = await apiPost<SessionResponse>("/auth/register", credentials);
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiPost<SessionResponse>("/auth/logout");
    } finally {
      // Meme si l'appel echoue (hors-ligne), on deconnecte localement.
      setUser(null);
    }
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({ user, login, register, logout }),
    [user, login, register, logout],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (context === null) {
    throw new Error("useSession doit être utilisé dans un SessionProvider");
  }
  return context;
}
